import Logger from '../lib/logger';
import PluginBase from './base';
import * as prebidFunctions from '../lib/prebid_helper';
import PrebidEvents from '../lib/prebid_events';
import ScriptLoader from '../lib/script_loader';
import Events, { EventTypes } from '../lib/events';
import { getCookie } from '../lib/cookies';

export default class RubiconDemandManagerBidding extends PluginBase {
  onBidManagerCreated() {
    if (this.biddingWithRDM()) {
      Logger.log('Installing plugin: Rubicon Demand Manager Bidding');

      if (!this.app.hasReinstalled) {
        this.definePbjs();
        this.loadRDMScript();
        Events.emit(EventTypes.rubiconDemandManagerBiddingSetUp);
      }

      // Check added for tests
      if (this.app.bidManager && typeof this.app.bidManager.addBiddingPartner === 'function') {
        this.app.bidManager.addBiddingPartner(this);
      } else {
        Logger.log('Rubicon Demand Manager: bidManager is not available, skipping addBiddingPartner');
      }
    }
  }

  /**
   * Determine whether we are using the Rubicon Demand Manager for bidding
   * @return {boolean}
   */
  biddingWithRDM() {
    return (
      this.app.settings.prebid.biddingEnabled &&
      this.app.settings.prebid.rubiconDemandManagerEnabled &&
      (this.app.settings.prebid.rubiconDemandManagerUrl ||
        this.app.settings.prebid.loadRDMScriptInConcertAds === false) &&
      this.hasOptOutConsent()
    );
  }

  /**
   * Define global pbjs namespace
   */
  definePbjs() {
    window.pbjs = window.pbjs || { que: [] };
  }

  /**
   * Check if we're not in an opt-in vs opt-out location
   * @return {boolean}
   */
  hasOptOutConsent() {
    const consentType = getCookie('_vm_consent_type');
    const canLoad = consentType !== 'opt-in';

    Logger.log(
      `Rubicon Demand Manager consent check: _vm_consent_type is "${consentType}", can load script: ${canLoad}`
    );

    // Load script unless we're explicitly in an opt-in location
    return canLoad;
  }

  /**
   * Loading the Rubicon Demand Manager script
   * We are using the Bid Manager beforeFirstBid method to make sure the script loads before we start bidding
   * @return {undefined}
   */
  loadRDMScript() {
    const dependency = {
      url: this.app.settings.prebid.rubiconDemandManagerUrl,
      timeout: this.app.settings.prebid.rdmScriptTimeout || 3000,
    };

    // Track when script loading starts for timing measurements
    // This timeout covers BOTH script loading AND waiting for pbjs.rp.requestBids
    const scriptLoadStartTime = Date.now();
    const totalTimeout = dependency.timeout;

    this.app.bidManager.beforeFirstBid(() => {
      return new Promise((resolve, reject) => {
        // In cases where the native page loads this script, we don't want to try to load it twice.
        // This fork allows us to only load when actually necessary.
        if (
          this.app.settings.prebid.loadRDMScriptInConcertAds === false ||
          document.querySelector(`script[src="${dependency.url}"]`)
        ) {
          const scriptLoadTime = Date.now() - scriptLoadStartTime;
          Logger.log(
            `Rubicon Demand Manager script at ${
              dependency.url
            } already exists in the DOM (script load time: ${scriptLoadTime}ms)`
          );
          this.setupAndListenForPrebidEvents();
          // Calculate remaining timeout for waiting for pbjs.rp.requestBids
          const remainingTimeout = Math.max(0, totalTimeout - scriptLoadTime);
          // Wait for pbjs.rp.requestBids to be ready since RDM is a main bid partner
          this.waitForRDMReady(remainingTimeout, totalTimeout, scriptLoadStartTime)
            .then(resolve)
            .catch(resolve);
        } else {
          new ScriptLoader()
            .load(dependency)
            .then(() => {
              const scriptLoadTime = Date.now() - scriptLoadStartTime;
              Logger.log(
                `Rubicon Demand Manager script ${
                  dependency.url
                } has loaded successfully (script load time: ${scriptLoadTime}ms).`
              );
              this.setupAndListenForPrebidEvents();
              // Calculate remaining timeout for waiting for pbjs.rp.requestBids
              const remainingTimeout = Math.max(0, totalTimeout - scriptLoadTime);
              // Wait for pbjs.rp.requestBids to be ready since RDM is a main bid partner
              return this.waitForRDMReady(remainingTimeout, totalTimeout, scriptLoadStartTime);
            })
            .then(resolve)
            .catch(error => {
              Logger.error(`Error loading Rubicon Demand Manager script: ${error}`);
              reject(error);
            });
        }
      });
    });
  }

  /**
   * Wait for pbjs.rp.requestBids to be ready before proceeding with auctions
   * Since RDM is a main bid partner, we should wait for it to be ready
   * @param {number} remainingTimeout Remaining time to wait (after script load) in milliseconds
   * @param {number} totalTimeout Total timeout covering both script load and waiting (for safety check)
   * @param {number} scriptLoadStartTime Timestamp when script loading started (for timing measurements)
   * @return {Promise}
   */
  waitForRDMReady(remainingTimeout = 3000, totalTimeout = 3000, scriptLoadStartTime = Date.now()) {
    return new Promise(resolve => {
      const waitStartTime = Date.now();
      const checkInterval = 20;
      let checkCount = 0;

      Logger.log(
        `[RDM Timing] Starting to wait for pbjs.rp.requestBids to be ready. Script load time so far: ${waitStartTime -
          scriptLoadStartTime}ms, Remaining timeout: ${remainingTimeout}ms`
      );

      const checkRDMReady = () => {
        checkCount++;
        const currentTime = Date.now();
        const timeSinceScriptLoad = currentTime - scriptLoadStartTime;
        const timeSinceWaitStart = currentTime - waitStartTime;

        // Check if pbjs exists and pbjs.rp.requestBids is ready
        if (typeof pbjs !== 'undefined' && pbjs.rp && typeof pbjs.rp.requestBids === 'function') {
          Logger.log(
            `[RDM Timing] ✅ Rubicon Demand Manager is ready! Total time from script load start: ${timeSinceScriptLoad}ms, Wait time: ${timeSinceWaitStart}ms, Checks performed: ${checkCount}`
          );
          resolve();
          return;
        }

        // Log progress every 50 checks (every ~1000ms) for visibility while limiting console noise
        if (checkCount % 50 === 0) {
          const pbjsExists = typeof pbjs !== 'undefined';
          const pbjsRpExists = pbjsExists && !!pbjs.rp;
          Logger.log(
            `[RDM Timing] Still waiting for pbjs.rp.requestBids... (${timeSinceWaitStart}ms elapsed since wait start, ${timeSinceScriptLoad}ms total, ${checkCount} checks, pbjs exists: ${pbjsExists}, pbjs.rp exists: ${pbjsRpExists})`
          );
        }

        // Check if we've exceeded the remaining timeout OR the total timeout (safety check)
        // Use whichever is smaller to ensure we don't exceed the total timeout
        const exceededRemainingTimeout = timeSinceWaitStart >= remainingTimeout;
        const exceededTotalTimeout = timeSinceScriptLoad >= totalTimeout;

        if (exceededRemainingTimeout || exceededTotalTimeout) {
          const pbjsExists = typeof pbjs !== 'undefined';
          const pbjsRpExists = pbjsExists && !!pbjs.rp;
          const reason = exceededTotalTimeout
            ? `total timeout of ${totalTimeout}ms`
            : `remaining timeout of ${remainingTimeout}ms`;
          Logger.log(
            `[RDM Timing] ⚠️ Rubicon Demand Manager did not become ready within ${reason}. Total time from script load: ${timeSinceScriptLoad}ms, Wait time: ${timeSinceWaitStart}ms, Checks performed: ${checkCount}. Proceeding with auctions (RDM will work on subsequent auctions). pbjs exists: ${pbjsExists}, pbjs.rp exists: ${pbjsRpExists}`
          );
          resolve(); // Resolve anyway to not block auctions indefinitely
          return;
        }

        // Check again after a short interval
        setTimeout(checkRDMReady, checkInterval);
      };

      // Start checking
      checkRDMReady();
    });
  }

  /**
   * Setting up Prebid Events so we can listen to them
   * We need to listen to Prebid Events even if we aren't setting up Prebid with Concert Ads
   */
  setupAndListenForPrebidEvents() {
    Logger.log('Prebid events are being set up.');
    this.app.prebidEvents = new PrebidEvents({ app: this.app });
    prebidFunctions.listenForEvents(this.app);
  }

  /**
   * Build array of slot objects ready for Prebid with Rubicon Demand Manager
   */
  buildBiddingQueues(queue) {
    const biddingQueue = [];

    queue.forEach(bid => {
      // Apply the shared request targeting gate before the GPT slot is read into the Demand
      // Manager request, so a refreshed slot's refresh/viewable values ride along in gptSlotObjects.
      bid.slot.applyRequestTargeting();
      biddingQueue.push(bid.slot.slot);
    });

    return biddingQueue;
  }

  /**
   * Fetch bids from Prebid with Rubicon Demand Manager
   * @type {Promise}
   */
  fetchBidsFor({ queueOfBids, timeout }) {
    let bidsFetched = false;

    return new Promise(resolve => {
      // Ensure promise always resolves, even if errors occur
      const safeResolve = () => {
        if (!bidsFetched) {
          bidsFetched = true;
          resolve();
        }
      };

      try {
        if (!prebidFunctions.prebidScriptLoaded()) {
          Logger.log(`The prebid script hasn't been loaded so we aren't fetching bids with RDM.`);
          return safeResolve();
        }

        // Check if pbjs exists before accessing it (pbjs is a global variable)
        if (typeof pbjs === 'undefined') {
          Logger.log(`pbjs is not available, skipping Rubicon Demand Manager bidding.`);
          return safeResolve();
        }

        const gptSlotObjects = this.buildBiddingQueues(queueOfBids);
        const gptSlotIds = gptSlotObjects.map(slot => slot?.getSlotElementId());
        Logger.log(`Fetching bids for the following slots with Rubicon: [${gptSlotIds}]`);

        const requestBids = () => {
          try {
            // Check if pbjs.rp.requestBids is available before calling
            if (!pbjs.rp || typeof pbjs.rp.requestBids !== 'function') {
              const rpExists = !!pbjs.rp;
              const requestBidsIsFunction = typeof pbjs.rp?.requestBids === 'function';
              Logger.log(
                `Rubicon Demand Manager requestBids is not available. pbjs.rp exists: ${rpExists}, requestBids is function: ${requestBidsIsFunction}`
              );
              safeResolve();
              return;
            }

            pbjs.rp.requestBids({
              callback: function() {
                Logger.log(`Rubicon Demand Manager bids have been fetched`);
                safeResolve();
              },
              gptSlotObjects,
              data: {
                keywords: {
                  keywords: this.app.variables.keywords,
                  permutive: this.app.variables.permutive,
                },
                inventory: {
                  position: this.getPositions(queueOfBids),
                  ...this.app.variables,
                },
              },
            });
          } catch (error) {
            Logger.log(`Error calling Rubicon Demand Manager requestBids: ${error}`);
            safeResolve();
          }
        };

        if (!pbjs.rp) {
          // Check if pbjs.que exists before using it
          if (!pbjs.que || typeof pbjs.que.push !== 'function') {
            Logger.log(`pbjs.que is not available, skipping Rubicon Demand Manager bidding.`);
            safeResolve();
            return;
          }

          pbjs.que.push(function() {
            try {
              // Check again when the queued function runs, in case RDM is still initializing
              // Note: pbjs is guaranteed to exist here (checked at line 245), but rp might not be ready yet
              if (!pbjs.rp || typeof pbjs.rp.requestBids !== 'function') {
                Logger.log(
                  `Rubicon Demand Manager requestBids is not available in queued function. Skipping RDM bidding.`
                );
                safeResolve();
                return;
              }
              requestBids();
            } catch (error) {
              Logger.log(`Error in Rubicon Demand Manager queued function: ${error}`);
              safeResolve();
            }
          });
        } else {
          requestBids();
        }

        // Set a failsafe timeout in case things go awry
        setTimeout(() => {
          if (!bidsFetched) {
            Logger.log(
              `Rubicon Demand Manager took longer than ${timeout} ms to fetch bids so we are calling the failsafe timeout`
            );
            safeResolve();
          }
        }, timeout);
      } catch (error) {
        // Catch any unexpected errors in the outer try block
        Logger.log(`Unexpected error in Rubicon Demand Manager fetchBidsFor: ${error}`);
        safeResolve();
      }
    });
  }

  /**
   * Add RDM targeting to bids
   */
  addTargeting() {
    if (typeof pbjs === 'undefined' || typeof pbjs.setTargetingForGPTAsync !== 'function') return;
    Logger.log('Adding Rubicon Demand Manager targeting to bids.');
    pbjs.setTargetingForGPTAsync();
  }

  /**
   * Get the position names of all of the slots being bidded on
   * @param {Array}
   * @return {Array}
   */
  getPositions(bids) {
    return bids.map(bid => bid.slot.data.name);
  }
}
